import type { CharitySuggestConfig, CharitySuggestElements, CharitySearchItem } from '../types/pifster-contracts';
import { searchCharities, submitSuggestion } from './suggest/suggest-api';
import { cacheSuggestElements } from './suggest/suggest-dom';
import {
  renderSearchResults,
  clearResults,
  setStatus,
  setHint,
  showLoadingSpinner,
  hideLoadingSpinner,
  setClearVisible,
  setSubmitEnabled,
} from './suggest/suggest-renderer';

// ---------------------------------------------------------------------------
// Turnstile types (local — only render + reset needed here)
// ---------------------------------------------------------------------------

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  reset: (widgetId: string) => void;
}

interface TurnstileRenderOptions {
  readonly sitekey: string;
  readonly callback: (token: string) => void;
  readonly 'expired-callback': () => void;
  readonly 'error-callback': () => void;
}

type SuggestWindow = Window & {
  turnstile?: TurnstileApi;
};

const SCRIPT_MODULE_DATA_ID = 'wp-script-module-data-pifster-charity-suggest';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function debounce<T extends (...args: never[]) => void>(fn: T, wait: number): T {
  let timer: ReturnType<typeof setTimeout> | undefined;
  return ((...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => { fn(...args); }, wait);
  }) as unknown as T;
}

function normalizeName(value: string): string {
  let name = value.trim().toLowerCase();
  name = name.replace(/[^a-z0-9\s-]/g, '');
  name = name.replace(/\s+/g, ' ');
  name = name.replace(/ /g, '-');
  return name;
}

function getBootstrapConfig(): CharitySuggestConfig | null {
  const moduleDataEl = document.getElementById(SCRIPT_MODULE_DATA_ID);
  if (!moduleDataEl) {
    console.error('[charity-suggest] Missing bootstrap configuration.');
    return null;
  }

  try {
    const raw = JSON.parse(moduleDataEl.textContent || '{}') as Partial<CharitySuggestConfig>;
    if (
      typeof raw.suggestEndpoint !== 'string' || raw.suggestEndpoint.trim() === '' ||
      typeof raw.searchEndpoint !== 'string' || raw.searchEndpoint.trim() === '' ||
      typeof raw.nonce !== 'string' || raw.nonce.trim() === '' ||
      typeof raw.turnstileSiteKey !== 'string'
    ) {
      console.error('[charity-suggest] Invalid bootstrap configuration.');
      return null;
    }

    return {
      suggestEndpoint: raw.suggestEndpoint,
      searchEndpoint: raw.searchEndpoint,
      nonce: raw.nonce,
      turnstileSiteKey: raw.turnstileSiteKey,
    };
  } catch {
    console.error('[charity-suggest] Failed to parse bootstrap configuration.');
    return null;
  }
}

function ensureTurnstileScriptLoaded(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const w = window as SuggestWindow;
    if (w.turnstile && typeof w.turnstile.render === 'function') {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-pifster-turnstile="1"]');
    if (existing) {
      existing.addEventListener('load', () => { resolve(); });
      existing.addEventListener('error', () => { reject(new Error('Failed to load Turnstile script')); });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-pifster-turnstile', '1');
    script.onload = () => { resolve(); };
    script.onerror = () => { reject(new Error('Failed to load Turnstile script')); };
    document.head.appendChild(script);
  });
}

// ---------------------------------------------------------------------------
// Main init
// ---------------------------------------------------------------------------

function init(): void {
  const bootstrapCfg = getBootstrapConfig();
  if (!bootstrapCfg) {
    return;
  }
  const cfg: CharitySuggestConfig = bootstrapCfg;

  const app = document.querySelector<HTMLElement>('#pifster-suggest-app');
  if (!app) {
    return;
  }

  const els: CharitySuggestElements = cacheSuggestElements(app);

  // ---- Mutable orchestrator state ----
  let selected: CharitySearchItem | null = null;
  let token = '';
  let widgetId: string | null = null;
  let inflight: AbortController | null = null;
  let searchRequestId = 0;
  let loadingTimer: ReturnType<typeof setTimeout> | undefined;
  const localCache: Record<string, CharitySearchItem[]> = {};

  const SPINNER_DELAY_MS = 120;
  const DEBOUNCE_MS = 180;
  const MIN_QUERY_LENGTH = 3;

  // ---- Loading spinner with delayed show ----

  function setLoading(isLoading: boolean): void {
    if (isLoading) {
      clearTimeout(loadingTimer);
      loadingTimer = setTimeout(() => { showLoadingSpinner(els); }, SPINNER_DELAY_MS);
      return;
    }

    clearTimeout(loadingTimer);
    loadingTimer = undefined;
    hideLoadingSpinner(els);
    setClearVisible(els, els.nameInput.value.trim().length > 0);
  }

  // ---- Search abort ----

  function abortInflightSearch(): void {
    searchRequestId += 1;
    if (inflight) {
      inflight.abort();
    }
    inflight = null;
  }

  // ---- Selection state ----

  function resetSelection(): void {
    selected = null;
    setHint(els, '');
    els.nameInput.removeAttribute('data-selected-post-id');
  }

  // ---- Debounced search ----

  const doSearch = debounce(() => {
    const q = els.nameInput.value.trim();
    resetSelection();

    if (q.length < MIN_QUERY_LENGTH) {
      abortInflightSearch();
      setLoading(false);
      setClearVisible(els, q.length > 0);
      clearResults(els);
      return;
    }

    let normalizedKey = normalizeName(q);
    if (normalizedKey === '') {
      normalizedKey = q.toLowerCase();
    }

    const cached = localCache[normalizedKey];
    if (cached) {
      setLoading(false);
      renderSearchResults(els, cached);
      return;
    }

    if (inflight) {
      inflight.abort();
    }

    const ac = new AbortController();
    inflight = ac;
    searchRequestId += 1;
    const requestId = searchRequestId;

    setLoading(true);

    searchCharities(q, cfg.searchEndpoint, ac.signal)
      .then((items) => {
        if (requestId !== searchRequestId) {
          return;
        }
        setLoading(false);
        localCache[normalizedKey] = items;
        renderSearchResults(els, items);
      })
      .catch(() => {
        if (requestId !== searchRequestId) {
          return;
        }
        setLoading(false);
        clearResults(els);
      });
  }, DEBOUNCE_MS);

  // ---- Event: input ----

  els.nameInput.addEventListener('input', () => {
    setStatus(els, '', '');
    setClearVisible(els, els.nameInput.value.trim().length > 0);
    doSearch();
  });

  // ---- Event: clear button ----

  if (els.clearBtn) {
    els.clearBtn.addEventListener('click', () => {
      abortInflightSearch();
      setLoading(false);
      setStatus(els, '', '');
      els.nameInput.value = '';
      setClearVisible(els, false);
      resetSelection();
      clearResults(els);
      els.nameInput.focus();
    });
  }

  // ---- Event: result selection ----

  els.resultsEl.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    const btn = target?.closest<HTMLButtonElement>('button[data-post-id]') ?? null;
    if (!btn) {
      return;
    }

    const postId = btn.getAttribute('data-post-id') ?? '';
    const name = btn.getAttribute('data-name') ?? '';
    const url = btn.getAttribute('data-url') ?? '';
    const status = btn.getAttribute('data-status') ?? '';

    selected = {
      post_id: Number(postId),
      charity_name: name,
      charity_url: url,
      charity_status: status,
      normalized_name: btn.getAttribute('data-normalized') ?? '',
    };

    els.nameInput.value = name;
    els.nameInput.setAttribute('data-selected-post-id', postId);

    if (url && els.urlInput) {
      els.urlInput.value = url;
    }

    if (status) {
      setHint(els, `This charity already exists (${status}). Submitting will register renewed interest.`);
    } else {
      setHint(els, '');
    }

    clearResults(els);
  });

  // ---- Event: outside click to close dropdown ----

  document.addEventListener('click', (e: Event) => {
    const target = e.target as Node | null;
    if (!els.resultsEl.hidden && target && !els.resultsEl.contains(target) && target !== els.nameInput) {
      clearResults(els);
    }
  });

  // ---- Turnstile lifecycle ----

  function mountTurnstile(): void {
    if (!cfg.turnstileSiteKey) {
      setStatus(els, 'Turnstile site key is missing.', 'error');
      return;
    }

    ensureTurnstileScriptLoaded()
      .then(() => {
        const ts = (window as SuggestWindow).turnstile;
        if (!ts || typeof ts.render !== 'function') {
          setStatus(els, 'Turnstile failed to initialize.', 'error');
          return;
        }

        widgetId = ts.render(els.turnstileEl, {
          sitekey: cfg.turnstileSiteKey,
          callback: (t: string) => {
            token = String(t ?? '');
            setSubmitEnabled(els, token.length > 0);
          },
          'expired-callback': () => {
            token = '';
            setSubmitEnabled(els, false);
          },
          'error-callback': () => {
            token = '';
            setSubmitEnabled(els, false);
          },
        });
      })
      .catch(() => {
        setStatus(els, 'Could not load Turnstile.', 'error');
      });
  }

  mountTurnstile();

  // ---- Event: form submission ----

  els.form.addEventListener('submit', (e: Event) => {
    e.preventDefault();

    const charityName = els.nameInput.value.trim();
    if (charityName.length < MIN_QUERY_LENGTH) {
      setStatus(els, 'Please enter a charity name.', 'error');
      return;
    }

    const suggesterName = els.suggesterNameInput?.value.trim() ?? '';
    const suggesterEmail = els.suggesterEmailInput?.value.trim() ?? '';

    if ((suggesterName !== '' && suggesterEmail === '') || (suggesterName === '' && suggesterEmail !== '')) {
      setStatus(els, 'Please provide both your name and email, or leave both blank.', 'error');
      return;
    }

    if (suggesterEmail !== '') {
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(suggesterEmail);
      if (!emailOk) {
        setStatus(els, 'Please enter a valid email address.', 'error');
        return;
      }
    }

    if (token === '') {
      setStatus(els, 'Please complete verification.', 'error');
      return;
    }

    setSubmitEnabled(els, false);
    setStatus(els, 'Submitting...', 'busy');

    const payload = {
      charity_name: charityName,
      charity_url: els.urlInput?.value.trim() ?? '',
      user_reason: els.reasonInput?.value.trim() ?? '',
      suggester_name: suggesterName,
      suggester_email: suggesterEmail,
      website: els.websiteInput?.value.trim() ?? '',
      turnstile_token: token,
    };

    submitSuggestion(payload, cfg.suggestEndpoint, cfg.nonce)
      .then(() => {
        setStatus(els, 'Thanks! Your suggestion has been recorded.', 'success');
        els.form.reset();
        resetSelection();
        clearResults(els);
        token = '';
        setSubmitEnabled(els, false);

        const ts = (window as SuggestWindow).turnstile;
        if (ts && widgetId !== null) {
          try {
            ts.reset(widgetId);
          } catch {
            // Turnstile reset may fail if widget was removed from DOM.
          }
        }
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Submission failed.';
        setStatus(els, message, 'error');
        setSubmitEnabled(els, token !== '');
      });
  });

  // Suppress unused-variable warning — selected is read inside the click handler closure.
  void selected;
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
